// src/services/notionClient.ts (プロキシ対応版)
import { ProjectData } from '../components/pages/ProjectDetail/projectData';

const DATABASE_ID = process.env.REACT_APP_NOTION_DATABASE_ID!;
// 本番環境では自身のドメインのWorkerを使用、開発環境ではローカルネットワークIP
const PROXY_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/notion'  // 本番: kosukeosada.com/api/notion (Cloudflare Worker)
  : 'http://192.168.10.120:3001/api/notion';  // 開発: ローカルプロキシサーバー（スマホからもアクセス可能）

// 画像URLをプロキシ経由に変換する関数
const proxyImageUrl = (imageUrl: string): string => {
  if (!imageUrl) return imageUrl;

  // すでにプロキシ経由の場合はそのまま返す
  if (imageUrl.includes('/api/notion/image')) {
    return imageUrl;
  }

  // Notion画像URLをプロキシ経由に変換
  if (imageUrl.includes('notion.so') || imageUrl.includes('amazonaws.com')) {
    const encodedUrl = encodeURIComponent(imageUrl);
    return `${PROXY_BASE_URL}/image?url=${encodedUrl}`;
  }

  // その他の画像はそのまま返す
  return imageUrl;
};

// Notion のプロパティから値を取得するヘルパー関数
const getPropertyValue = (properties: any, propertyName: string): any => {
  const property = properties[propertyName];
  if (!property) return null;

  switch (property.type) {
    case 'title':
      return property.title[0]?.plain_text || '';
    case 'rich_text':
      return property.rich_text[0]?.plain_text || '';
    case 'number':
      return property.number;
    case 'select':
      return property.select?.name || '';
    case 'status':
      return property.status?.name || '';
    case 'multi_select':
      return property.multi_select?.map((item: any) => item.name) || [];
    case 'date':
      return property.date?.start || null;
    case 'url':
      return property.url;
    case 'files':
      return property.files.map((file: any) =>
        file.type === 'external' ? file.external.url : file.file.url
      );
    case 'created_time':
    case 'last_edited_time':
      return property[property.type];
    case 'created_by':
    case 'last_edited_by':
      return property[property.type];
    default:
      return null;
  }
};

// Notion のページデータを ProjectData 形式に変換
const transformNotionToProject = (page: any): ProjectData => {
  const properties = page.properties;
  
  // date プロパティから年を取得
  const dateProperty = getPropertyValue(properties, 'date');
  const year = dateProperty ? new Date(dateProperty).getFullYear().toString() : '';
  
  // categories から カテゴリを取得（マルチセレクト対応）
  const categoryArray = getPropertyValue(properties, 'categories') || [];
  const category = Array.isArray(categoryArray) && categoryArray.length > 0
    ? categoryArray // 配列全体を返す
    : ['no category']; // デフォルトも配列として返す
  
  // description_image を images 配列の最初の要素として使用
  const descriptionImage = getPropertyValue(properties, 'description_image');
  const thumbnailImage = getPropertyValue(properties, 'thumbnail');
  
  // 画像配列を構築（thumbnail と description_image を含む）
  const images = [];
  if (thumbnailImage && thumbnailImage.length > 0) {
    images.push(...thumbnailImage.map(proxyImageUrl));
  }
  if (descriptionImage && descriptionImage.length > 0) {
    images.push(...descriptionImage.map(proxyImageUrl));
  }
  
  const siteUrl = getPropertyValue(properties, 'website_url');
  const appStoreUrl = getPropertyValue(properties, 'appstore_url');

  // クレジット情報を取得（rich_textプロパティから取得し、JSON形式で解析）
  const creditsText = getPropertyValue(properties, 'credits');
  let credits = [];

  if (creditsText) {
    try {
      // JSON形式のクレジット情報をパース
      credits = JSON.parse(creditsText);
    } catch (e) {
      // JSONパースに失敗した場合、デフォルトのクレジット情報を設定
      console.warn('Failed to parse credits, using default');
      credits = [];
    }
  }

  return {
    title: getPropertyValue(properties, 'name') || '',
    category: category, // 配列として返す
    year: year,
    client: 'Client Name', // 固定値または別途設定
    description: getPropertyValue(properties, 'description_text') || '',
    images: images,
    specs: [], // 必要に応じて別途取得
    videoUrl: getPropertyValue(properties, 'video_url'),
    url: siteUrl,
    appStoreUrl: appStoreUrl,
    credits: credits,
    status: getPropertyValue(properties, 'status'),
  };
};

// 全てのプロジェクトを取得
export const fetchAllProjects = async (): Promise<Record<string, ProjectData>> => {
  try {
    const response = await fetch(`${PROXY_BASE_URL}/database/${DATABASE_ID}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filter: {
          property: 'delete_flag',
          checkbox: {
            equals: false
          }
        },
        sorts: [
          {
            property: 'created_time',
            direction: 'descending'
          }
        ]
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const projects: Record<string, ProjectData> = {};
    
    data.results.forEach((page: any) => {
      const projectId = page.id; // ページIDをプロジェクトIDとして使用
      projects[projectId] = transformNotionToProject(page);
    });

    return projects;
  } catch (error) {
    console.error('Failed to fetch projects from Notion:', error);
    throw error;
  }
};

// 特定のプロジェクトを取得
export const fetchProject = async (projectId: string): Promise<ProjectData | null> => {
  try {
    const response = await fetch(`${PROXY_BASE_URL}/page/${projectId}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const page = await response.json();
    return transformNotionToProject(page);
  } catch (error) {
    console.error(`Failed to fetch project ${projectId} from Notion:`, error);
    return null;
  }
};

// プロジェクト一覧（簡易版）を取得
export const fetchProjectList = async () => {
  try {
    const url = `${PROXY_BASE_URL}/database/${DATABASE_ID}/query`;
    console.log('Fetching from:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filter: {
          property: 'delete_flag',
          checkbox: {
            equals: false
          }
        },
        sorts: [
          {
            property: 'created_time',
            direction: 'descending'
          }
        ]
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Notion API Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    console.log('Notion API Response:', data);

    return data.results.map((page: any) => {
      const properties = page.properties;
      const thumbnailImages = getPropertyValue(properties, 'thumbnail') || [];
      const defaultImage = 'https://picsum.photos/120/120?random=1';
      
      // マルチセレクトのカテゴリを取得（配列として）
      const categoryArray = getPropertyValue(properties, 'categories') || [];
      const category = Array.isArray(categoryArray) && categoryArray.length > 0
        ? categoryArray
        : ['no category'];

      return {
        id: page.id, // ページIDを使用
        title: getPropertyValue(properties, 'name') || 'Untitled',
        category: category, // 配列として返す
        image: proxyImageUrl(thumbnailImages[0] || defaultImage)
      };
    });
  } catch (error) {
    console.error('Failed to fetch project list from Notion:', error);
    // エラーの詳細をログ
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
    }
    throw error;
  }
};